import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

const t = buildTranslate('recovery_ai.case_detail.actions')

const RESTRICT_DISABLED_REASON_ID = 'case-actions-menu-restrict-disabled-reason'

type Props = {
  // The manage-MerchantSettings + manage-Feedback gate shared with the
  // sibling Grant Reward / Mark Resolved buttons in index.tsx
  // (feedbackGatePermissionDisabled/-Reason there).
  permissionDisabled: boolean
  permissionReason: string | undefined
  // The customer may already be restricted from a DIFFERENT case — restricting
  // doesn't necessarily close every one of their cases (index.tsx comment).
  isAlreadyRestricted: boolean
  // A resolve mutation in flight — the same in-flight gate the sibling
  // buttons share, so a double-click can't fire concurrent writes.
  isResolving: boolean
  onRestrictClick: () => void
}

// Case-detail header overflow menu. Currently a single item ("Restrict this
// customer"), but built as a full WAI-ARIA menu-button (role="menu" +
// role="menuitem"), not a bare button, to match the target design and stay
// consistent at any future item count with the two established hand-rolled
// overflow-menu implementations this mirrors closely:
// RestrictedCustomers/RowActionsMenu and
// OperationsCenter/Locations/LocationsTab/RowOverflowMenu.
//
// Native <button> elements are used DELIBERATELY here (not @thanx/uikit
// <Button>) — this is a justified exemption, not a convention miss, for the
// same reason as those two references:
//   1. The WAI-ARIA menu-button pattern requires `role="menu"`/
//      `role="menuitem"`, `aria-haspopup`, `aria-expanded`, `aria-controls`,
//      `aria-label`, `aria-disabled`, and `id` on the controls. uikit's
//      Button prop type does NOT expose `role`/`aria-*`/`id`, so it cannot
//      express this pattern.
//   2. Shrinking a uikit Button to a borderless menu item via
//      `overrides.BaseButton` REPLACES uikit's own minimal styling (uikit
//      spreads `...overrides` last), collapsing it to baseui's
//      filled-primary default.
// The controls remain fully a11y-correct (semantic buttons, keyboard nav,
// aria labelling).
const CaseActionsMenu = ({
  permissionDisabled,
  permissionReason,
  isAlreadyRestricted,
  isResolving,
  onRestrictClick,
}: Props) => {
  const [css] = useStyletron()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const closeMenu = () => {
    setOpen(false)
    // Return focus to the trigger so a keyboard user isn't stranded on a
    // hidden element after Escape / outside-click.
    triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!open) return undefined
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) closeMenu()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Focus the first menuitem on open so a keyboard user lands inside the menu
  // and the arrow-key handler has somewhere to move from.
  useEffect(() => {
    if (!open) return
    const firstItem =
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')
    firstItem?.focus()
  }, [open])

  // WAI-ARIA menu keyboard nav: Arrow keys move between menuitems (wrapping
  // at the boundaries), Home/End jump to the first/last. Works at any item
  // count, including today's single item.
  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return
    e.preventDefault()
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []
    )
    if (items.length === 0) return
    const currentIndex = items.indexOf(document.activeElement as HTMLElement)
    let nextIndex = currentIndex
    if (e.key === 'ArrowDown') {
      nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
    } else if (e.key === 'ArrowUp') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
    } else if (e.key === 'Home') {
      nextIndex = 0
    } else if (e.key === 'End') {
      nextIndex = items.length - 1
    }
    items[nextIndex]?.focus()
  }

  const menuId = 'case-detail-actions-menu'
  const menuLabel = t('menu_aria')

  // Precedence, carried over 1:1 from the pre-refactor button (index.tsx
  // review comment): the permission gate wins outright — when it's the one
  // blocking, the already-restricted styling/reason is reserved for the case
  // where permission is fine and only the restricted state blocks the click.
  const alreadyRestrictedBlocked = !permissionDisabled && isAlreadyRestricted
  const isItemDisabled =
    permissionDisabled || isResolving || alreadyRestrictedBlocked

  const handleItemClick = () => {
    // Guard rather than the native `disabled` attribute: a natively disabled
    // element drops out of the tab order, so a keyboard/AT user could never
    // reach the disabled reason (a11y checklist: disabled-with-tooltip must
    // stay keyboard-reachable). Mirrors RowActionsMenu's aria-disabled
    // pattern for the same reason.
    if (isItemDisabled) return
    onRestrictClick()
    setOpen(false)
  }

  const itemClass = css({
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    fontSize: '14px',
    ':focus-visible': {
      outline: `2px solid ${merchantTheme.colors.belizeHole50}`,
    },
  })

  const restrictItem = (
    <button
      type="button"
      role="menuitem"
      aria-disabled={isItemDisabled || undefined}
      aria-describedby={
        alreadyRestrictedBlocked ? RESTRICT_DISABLED_REASON_ID : undefined
      }
      title={
        alreadyRestrictedBlocked ? t('restrict_disabled_reason') : undefined
      }
      onClick={handleItemClick}
      className={`${itemClass} ${css(
        isItemDisabled
          ? { color: merchantTheme.colors.grey50, cursor: 'not-allowed' }
          : {
              color: merchantTheme.colors.cayenne50,
              cursor: 'pointer',
              ':hover': { backgroundColor: merchantTheme.colors.grey05 },
            }
      )}`}
    >
      {t('restrict')}
    </button>
  )

  return (
    <div
      ref={containerRef}
      // `position: relative` is only here to anchor the dropdown menu's
      // `right: 0` below to the trigger itself. Vertical centering against
      // the sibling uikit Buttons (Grant reward / Send message / Mark as
      // resolved) is handled by `align-items-center` on the shared flex row
      // in index.tsx, not here — this container has no intrinsic height of
      // its own, so a height/align-items hack on it was fighting the row's
      // real cross-axis alignment instead of just deferring to it.
      className={css({ position: 'relative' })}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={menuLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen(prev => !prev)}
        className={css({
          // `position: relative` anchors the icon's absolute centering below;
          // `padding: 0` strips the browser's default <button> padding
          // (asymmetric top/bottom in some UAs), which combined with the fa
          // glyph's own line-box metrics is what pinned the dots off-center
          // — the icon below is centered by absolute-positioning + transform
          // instead, which is immune to both.
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          padding: 0,
          // Bare dots at rest (no fill) per the target design; a light grey
          // fill only appears on hover/open, not persistently.
          background: open ? merchantTheme.colors.grey20 : 'transparent',
          border: 'none',
          borderRadius: '6px',
          color: merchantTheme.colors.grey70,
          cursor: 'pointer',
          ':hover': { background: merchantTheme.colors.grey10 },
        })}
      >
        <i
          className={`fa fa-ellipsis-h ${css({
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '16px',
            lineHeight: 1,
          })}`}
          aria-hidden="true"
        />
      </button>
      {/* sr-only reason for the disabled item when it's the already-restricted
          state doing the blocking — keyboard/AT-reachable via
          aria-describedby (not hover/title-only). */}
      {alreadyRestrictedBlocked && (
        <span id={RESTRICT_DISABLED_REASON_ID} className="sr-only">
          {t('restrict_disabled_reason')}
        </span>
      )}
      {open && (
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          aria-label={menuLabel}
          onKeyDown={onMenuKeyDown}
          className={css({
            position: 'absolute',
            top: '100%',
            // Anchored to the trigger's right edge, extending leftward — the
            // target design opens below-and-left so the menu never overlaps
            // Grant reward / Send message to its right.
            right: '0',
            marginTop: '4px',
            minWidth: '220px',
            backgroundColor: merchantTheme.colors.white,
            border: `1px solid ${merchantTheme.colors.grey30}`,
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            zIndex: 10,
          })}
        >
          <PermissionTooltip
            disabled={permissionDisabled}
            reason={permissionReason}
            id="case-detail-actions-menu-restrict-perm"
            placement="right"
          >
            {restrictItem}
          </PermissionTooltip>
        </div>
      )}
    </div>
  )
}

export default CaseActionsMenu
